/**
 * UI Mode store — the universal Classic / Frontier toggle (Bob 2026-06-11).
 *
 * This is a single global POSTURE, not a per-page skin. It changes the app's
 * center of gravity:
 *   - "frontier": the app opens on JUDGMENT — briefing-first home, Felix
 *     forward, the synthesized "what needs you now" surfaces lead. The
 *     AI-native default.
 *   - "classic":  the app opens on RECORDS — dashboard widgets, entity grids,
 *     the full nav. The always-there escape hatch.
 *
 * Deliberately NOT a per-page flag: most pages are records (forms, settings,
 * single entities) and look identical in both modes because there is nothing
 * to synthesize there. The mode only elevates the finite set of genuine
 * judgment surfaces (home/briefing, portfolio, project overview, operate,
 * calendar). Surfaces consult `useUiMode()` to decide their default
 * presentation; everything else ignores it.
 *
 * Persisted per user in localStorage so the choice sticks across sessions.
 */
import { create } from "zustand";
import { persist } from "zustand/middleware";

export type UiMode = "classic" | "frontier";

interface UiModeState {
  mode: UiMode;
  setMode: (mode: UiMode) => void;
  toggle: () => void;
}

export const useUiMode = create<UiModeState>()(
  persist(
    (set, get) => ({
      // AI-native default; Classic is the safety net, never the other way round.
      mode: "frontier",
      setMode: (mode) => set({ mode }),
      toggle: () => set({ mode: get().mode === "frontier" ? "classic" : "frontier" }),
    }),
    {
      name: "ftl-ui-mode",
      // T3 one-home collapse (2026-07-11): the FrontierToggle UI was
      // removed, leaving the store with NO remaining setMode/toggle caller —
      // a previously persisted "classic" would be unreachable dead state
      // (stuck lead-strip-less forever). The version bump migrates any
      // persisted value back to the hard default "frontier"; the ~20
      // surfaces that consult the store for Frontier lead strips keep
      // working, they just can't be flipped off anymore.
      version: 1,
      migrate: () => ({ mode: "frontier" as UiMode }),
    },
  ),
);

/** True when the app should lead with the AI-native judgment surfaces. */
export const isFrontier = (mode: UiMode): boolean => mode === "frontier";
